import { FaCheck } from '@meronex/icons/fa';
import { MdcAlertCircleCheckOutline } from '@meronex/icons/mdc';
import { useMount } from 'ahooks';
import React, { useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import Select, {
  SingleValue,
  components,
  MenuProps,
  MultiValue,
} from 'react-select';
import { Box } from '@apia/theme';
import ApiaApi, { makeApiaUrl } from '../../../../../api/apiaApi/apiaApi';
import { useAppSelector } from '../../../../../hooks/hooks';
import store from '../../../../../store';
import propsStore from '../../../../../store/propsStore/propsStore';
import { translationActions } from '../../../../../store/translationSlice';
import validationsStore from '../../../../../store/validation/validationStore';
import { toBoolean } from '../../../../../utils/util';
import { useFormContext } from '../../../../common/validation/FormContext';
import { ILang } from './TranslateModal';

interface ILanguageList {
  langs: ILang[];
  onClick: () => void;
  fieldId: string;
}

export interface ILanguageState extends ILang {
  fieldId: string;
  hasQueriedServer: boolean;
  isTranslated?: boolean;
  currentValue?: string;
  isTemporalyTranslated?: boolean;
}

export type TFieldLanguage = {
  langs: ILanguageState[];
  selectedLanguage: string;
};

export function parseFieldXmlTrad(fakeJSON: string) {
  return [...fakeJSON.matchAll(/(\d+):\s*(true|false)/g)].map(([, k, v]) => ({
    id: k,
    label: v,
  }));
}

const Menu = (props: MenuProps<ILanguageState>) => {
  const [ref, setRef] = React.useState<HTMLInputElement | null>(null);
  const [position, setPosition] = React.useState({ left: -1000, top: -1000 });

  React.useEffect(() => {
    if (!ref) return;
    const getPosition = ref.getBoundingClientRect();
    setPosition({ left: getPosition.left, top: getPosition.top });
  }, [ref]);

  return (
    <Box ref={setRef}>
      <Box
        sx={React.useMemo(
          () => ({
            position: 'fixed',
            zIndex: 'tooltip',
            width: '300px',
            ...position,
          }),
          [position],
        )}
      >
        <components.Menu {...props}>{props.children}</components.Menu>
      </Box>
    </Box>
  );
};

const LanguageList = ({ langs, onClick, fieldId }: ILanguageList) => {
  const [selectedOption, setSelectedOption] = React.useState<ILanguageState>(
    {} as ILanguageState,
  );
  const selectedLangId = useAppSelector(
    (global) =>
      global.translationSlice[fieldId]?.selectedLanguage?.id as string,
  );

  const languages = useAppSelector(
    (global) =>
      (global.translationSlice[fieldId]?.langs ??
        []) as unknown as ILanguageState[],
    shallowEqual,
  );

  const parseLanguagesForSelect = useAppSelector((global) =>
    global.translationSlice[fieldId]?.langs.map((current) => {
      return {
        ...current,
        value: current.id,
        label: current.label,
        isTranslated: current.isTranslated,
        fieldId: current.fieldId,
      };
    }),
  );

  const { name } = useFormContext();
  useEffect(() => {
    const languageSelected = languages.find(
      (current) => String(current.id) === selectedLangId,
    );
    if (!languageSelected) return;
    setSelectedOption({
      ...languageSelected,
      isTemporalyTranslated: languageSelected?.isTranslated
        ? false
        : languageSelected?.isTemporalyTranslated,
    });
  }, [languages, selectedLangId]);

  useMount(() => {
    async function initialLoad() {
      if (store.getState().translationSlice[fieldId]?.langs) {
        const valueTranslation = await getFieldTranslations(
          String(langs[0].id),
        );
        store.dispatch(
          translationActions.setState({
            id: fieldId,
            selectedLanguage: {
              id: String(langs[0]?.id),
              label: String(langs[0]?.label),
            },
          }),
        );
        if (valueTranslation) {
          validationsStore.setFieldValue(
            name,
            String(langs[0].id),
            valueTranslation,
          );
          translationActions.markIsTranslated({
            id: fieldId,
            langId: String(langs[0].id),
            isTranslated: true,
          });
          store.dispatch(
            translationActions.setCurrentValue({
              id: fieldId,
              langId: String(langs[0].id),
              currentValue: valueTranslation,
            }),
          );
        }
        setSelectedOption(languages[0]);

        return;
      }
      const props = propsStore.getFieldProps(fieldId);
      const initialTranslations = parseFieldXmlTrad(props.field.trad ?? '');

      const initialLangs = langs.map((current) => {
        const definition: ILanguageState = {
          ...current,
          fieldId,
          hasQueriedServer: false,
          isTranslated:
            initialTranslations.findIndex(
              (search) =>
                String(search.id) === String(current.id) &&
                toBoolean(search.label) === true,
            ) !== -1,
        };

        return definition;
      });

      if (langs[0]) {
        const firstLangTranslation = await getFieldTranslations(
          String(langs[0].id),
        );

        initialLangs[0].hasQueriedServer = true;
        initialLangs[0].isTranslated = !!firstLangTranslation;
        initialLangs[0].currentValue = firstLangTranslation;

        setSelectedOption(initialLangs[0]);

        validationsStore.setFieldValue(
          name,
          String(langs[0].id),
          firstLangTranslation,
        );
      }

      store.dispatch(
        translationActions.setState({
          id: fieldId,
          langs: initialLangs,
          selectedLanguage: {
            id: String(langs[0]?.id),
            label: String(langs[0]?.label),
          },
        }),
      );
    }
    void initialLoad();
  });

  const getFieldTranslations = React.useCallback(
    async (langId: string) => {
      const props = propsStore.getFieldProps(fieldId);
      const result = await ApiaApi.post<{ values: { value: string } }>(
        makeApiaUrl({
          ajaxUrl: '/apia.execution.FormAction.run',
          action: 'getFieldTranslations',
          frmId: props.formData?.frmId,
          frmParent: props.formData?.frmParent,
          fldId: props.field.id,
          attId: props.field.attId,
          index:
            props.attributes.fieldType !== 'editor' &&
            props.attributes.fieldType !== 'area'
              ? props.attributes.index
              : undefined,
          langId,
        }),
      );
      if (
        (result?.data?.values.value as string) !== 'null' ||
        !(result?.data?.values.value as string)
      ) {
        return result?.data?.values.value as string;
      }
      return '';
    },
    [fieldId],
  );

  const onChangeHandler = React.useCallback(
    (eventValue: MultiValue<ILanguageState> | SingleValue<ILanguageState>) => {
      const selectedLanguage = eventValue as SingleValue<ILanguageState>;
      if (selectedLanguage === null) return;
      setSelectedOption(selectedLanguage);
      store.dispatch(
        translationActions.setState({
          id: fieldId,
          selectedLanguage: {
            id: String(selectedLanguage?.id),
            label: selectedLanguage.label,
          },
        }),
      );

      if (!selectedLanguage.hasQueriedServer)
        void getFieldTranslations(String(selectedLanguage?.id))
          .then((initialValue) => {
            validationsStore.setFieldValue(
              name,
              String(selectedLanguage.id),
              initialValue ?? '',
            );
            store.dispatch(
              translationActions.setCurrentValue({
                id: fieldId,
                langId: String(selectedLanguage.id),
                currentValue: initialValue,
              }),
            );
            store.dispatch(
              translationActions.markAsQueried({
                id: fieldId,
                langId: String(
                  store.getState().translationSlice[fieldId]?.selectedLanguage,
                ),
                hasQueriedServer: true,
              }),
            );
            store.dispatch(
              translationActions.markIsTranslated({
                id: fieldId,
                langId: String(
                  store.getState().translationSlice[fieldId]?.selectedLanguage,
                ),
                isTranslated: !!initialValue,
              }),
            );
            setSelectedOption({
              ...selectedLanguage,
              hasQueriedServer: true,
              isTranslated: !!initialValue,
            });
          })
          .catch();

      onClick();
    },
    [fieldId, getFieldTranslations, name, onClick],
  );

  return (
    <Box className="languageList">
      <Select
        classNamePrefix="react-select"
        isSearchable={false}
        className="languageList__languageSelection"
        value={selectedOption}
        onChange={onChangeHandler}
        options={parseLanguagesForSelect}
        components={{ Menu }}
        getOptionLabel={(currentLanguage) => {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <>
                {currentLanguage?.isTranslated ? <FaCheck size={16} /> : null}

                {currentLanguage?.isTemporalyTranslated ? (
                  <>
                    <MdcAlertCircleCheckOutline size={20} />
                  </>
                ) : null}

                <Box as="span" sx={{ marginLeft: 5, width: '200px' }}>
                  {currentLanguage.label}
                </Box>
              </>
            </Box>
          ) as unknown as string;
        }}
      />
      <Box as="span">
        {`${window.LBL_TRANSLATIONS}:${' '} 
        ${
          parseLanguagesForSelect?.filter(
            (current) => current.isTranslated === true,
          ).length
        }(${
          parseLanguagesForSelect?.filter(
            (current) => !current.isTranslated && current.isTemporalyTranslated,
          ).length
        })`}
      </Box>
    </Box>
  );
};

export default LanguageList;
